import DarkButton from "components/actions/DarkButton";
import GrayButton from "components/actions/GrayButton";
import DatePicker from "components/fields/DatePicker";
import InputField from "components/fields/InputField";
import Select from "components/fields/Select";
import { useEffect, useState } from "react";
import { DrawerHeader, DrawerBody } from "@chakra-ui/modal";
import AssignForm from "./assign-form";
import { useSearchParams } from "react-router-dom";
import AtriumApi from "api";
import AlertPane from "components/alert";
// import EditableSelect from "components/fields/EditableSelect";
import MapboxAddressInput from "components/mapbox";
import toast from "react-hot-toast";
import Loader from "components/loader/Loader";
import CreatableSelect from "react-select/creatable";
import { MdOutlineClear } from "react-icons/md";
import ConfirmationDialog from "../settings/confirmation-dialog";
import { useDarkmode } from "hooks/useTheme";

const SetupForm = ({ service, errors, setErrors, setService, type }) => {
  const [template, setTemplate] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [loading, setLoading] = useState(false);

  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  const { darkmode } = useDarkmode();

  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    message: "",
    onConfirm: () => {},
  });

  // const [sericeTypeOptions, setServiceTypeOptions] = useState([]);

  // const searchServiceTypes = (query, callback) => {
  //   AtriumApi.get("/v1/service-types", { params: { q: query } }).then(
  //     (resp) => {
  //       const options = resp.data.serviceTypes.map((t) => ({
  //         label: t.name,
  //         value: t.name,
  //       }));
  //       setServiceTypeOptions(options);
  //       callback(options);
  //     }
  //   );
  // };

  // select the type
  useEffect(() => {
    const getSelectedTypes = async () => {
      if (!type) return;
      try {
        setLoading(true);
        const response = await AtriumApi.get("/v1/templates");
        if (response) {
          setLoading(false);
          setTemplate(response?.data?.data);
        }
      } catch (error) {
        setLoading(false);
        console.log("error while fetching the selected types", error);
        toast.error(error?.response?.data?.message);
      }
    };

    getSelectedTypes();
  }, [type]);

  // select the service type

  useEffect(() => {
    if (!selectedTemplate) return;

    const getServiceType = async () => {
      try {
        setLoading(true);
        const response = await AtriumApi.get(
          `/v1/services/${selectedTemplate}`
        );
        if (response?.data?.service) {
          setLoading(false);
          const s = response?.data?.service;

          setService({
            name: s?.name || "",
            description: s?.description || "",
            type: s?.type || "",
            address: s?.address || "",
            // date: s?.date || "",
            startAt: s?.startAt || "",
            endAt: s?.endAt || "",
            isEdit: true,
          });
        }
      } catch (error) {
        setLoading(false);
        console.log("error while fetching the service type", error);
      }
    };
    getServiceType();
  }, [selectedTemplate, setService]);

  // Fetch options from API
  const fetchServiceTypes = async () => {
    try {
      const response = await AtriumApi.get("/v2/service-types");

      const fetched = response?.data?.data?.map((item) => ({
        value: item?.id,
        label: item?.serviceType,
      }));
      setOptions(fetched || []);
    } catch (err) {
      toast.error("Failed to fetch service types.");
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchServiceTypes();
  }, []);

  // Handle selection
  const handleChange = (selectedOption) => {
    // setService({ ...service, type: selectedOption?.value || "" });
    setService({ ...service, type: selectedOption?.label || "" });
    if (errors.type) {
      setErrors((prev) => ({ ...prev, type: "" }));
    }
  };

  // Handle creating new option
  const handleCreate = async (inputValue) => {
    setIsLoading(true);
    setMenuOpen(true); // Keep dropdown open
    try {
      await AtriumApi.post("/v2/service-types", {
        serviceType: inputValue,
      });
      fetchServiceTypes();
      toast.success("Service type created successfully.");
    } catch (err) {
      toast.error("Failed to create service type.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle delete from API
  const handleDelete = async (id) => {
    setConfirmationDialog({
      open: true,
      message: "You want to delete this service type?",
      onConfirm: async () => {
        try {
          await AtriumApi.delete(`/v2/service-types/${id}`);
          fetchServiceTypes();
          toast.success("Service type deleted successfully.");
          setMenuOpen(true); // Keep dropdown open
        } catch (err) {
          toast.error("Failed to delete service type.");
        } finally {
          setConfirmationDialog({
            open: false,
            message: "",
            onConfirm: () => {},
          });
        }
      },
    });
  };

  // MM/DD/YYYY format

  function formatToMMDDYYYY(date) {
    if (!date) return "";
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, "0"); // months are 0-based
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  }

  // 12 hours format time
  function formatTo12Hour(hour, minute) {
    const h = hour % 12 || 12;
    const m = String(minute).padStart(2, "0");
    const period = hour < 12 ? "AM" : "PM";
    return `${String(h).padStart(2, "0")}:${m} ${period}`; // always "02:00 PM"
  }

  // Custom option renderer with delete icon
  const CustomOption = (props) => {
    const { data, innerRef, innerProps } = props;
    const isDeletable = typeof data.value === "number"; // Only allow delete for saved options

    return (
      <div
        ref={innerRef}
        {...innerProps}
        className="flex justify-between items-center px-3 py-2 hover:bg-gray-100 cursor-pointer"
      >
        <span>{data.label}</span>
        {isDeletable && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(data.value);
            }}
          >
            <MdOutlineClear />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {loading ? (
        <div className="flex items-center justify-center h-full w-full">
          <Loader />
        </div>
      ) : (
        <>
          {type === "create" && (
            <div className="flex flex-col">
              <label
                className="text-sm text-navy-700 dark:text-white ml-2 mb-2"
                htmlFor="template"
              >
                Select template
              </label>

              <Select
                id="template"
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                required
              >
                <option hidden disabled value="">
                  -- Select a template
                </option>
                {template?.length > 0 ? (
                  template?.map((data) => (
                    <option key={data?.id} value={data?.id}>
                      {data?.tempName}
                    </option>
                  ))
                ) : (
                  <option hidden disabled value="">
                    No template found
                  </option>
                )}
              </Select>
            </div>
          )}

          <InputField
            variant="service_form"
            label="Service name"
            placeholder="Select a name"
            id="name"
            type="text"
            extra="flex flex-col pt-4"
            value={service?.name || ""}
            onChange={(e) => {
              setService({ ...service, name: e.target.value });
              if (errors.name) {
                setErrors((prev) => ({ ...prev, name: "" }));
              }
            }}
          />
          {errors.name && (
            <p className="text-red-500 text-sm mt-1">{errors.name}</p>
          )}

          <InputField
            variant="service_form"
            label="Description"
            placeholder="Write a description"
            id="description"
            type="text"
            extra="flex flex-col pt-4"
            value={service?.description || ""}
            onChange={(e) => {
              setService({ ...service, description: e.target.value });
              if (errors.description) {
                setErrors((prev) => ({ ...prev, description: "" }));
              }
            }}
          />
          {errors.description && (
            <p className="text-red-500 text-sm mt-1">{errors.description}</p>
          )}

          {/* <div className="flex flex-col pt-4">
            <label
              className="text-sm text-navy-700 dark:text-white ml-2 mb-2"
              htmlFor="service-type"
            >
              Service type
            </label>
            <Select
              id="service-type"
              value={service?.type || ""}
              onChange={(e) => {
                setService({ ...service, type: e.target.value });
                if (errors.type) {
                  setErrors((prev) => ({ ...prev, type: "" }));
                }
              }}
            >
              <option hidden disabled value="">
                -- Select a service type
              </option>
              {!selectedTemplate && serviceTypeOptions?.length === 0 ? (
                <option value="" disabled>
                  No service type found
                </option>
              ) : (
                serviceTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))
              )}
            </Select>
            {errors.type && (
              <p className="text-red-500 text-sm mt-1">{errors.type}</p>
            )}
          </div> */}

          <div className="flex flex-col pt-4">
            <label
              className="text-xs text-navy-700 dark:text-white ml-2 mb-2"
              htmlFor="service-type"
            >
              Service type
            </label>

            <CreatableSelect
              // isClearable
              isLoading={isLoading}
              options={options}
              onChange={handleChange}
              onCreateOption={handleCreate}
              placeholder="-- Select or create service type"
              // value={options.find((opt) => opt.value === service?.type) || null}
              value={
                service?.type
                  ? options.find((opt) => opt.label === service?.type) || {
                      label: service?.type,
                      value: service?.type,
                    }
                  : null
              }
              components={{ Option: CustomOption }}
              onMenuOpen={() => setMenuOpen(true)}
              onMenuClose={() => setMenuOpen(false)}
              menuIsOpen={menuOpen}
              styles={{
                control: (base, state) => ({
                  ...base,
                  borderRadius: "1rem",
                  fontSize: "0.875rem",
                  borderColor: darkmode
                    ? "rgb(112 126 174 / 1)"
                    : "rgb(218 222 236 / 1)",
                  backgroundColor: darkmode ? "transparent" : "#fff",
                  // color:  "#111827",
                  height: 40,
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 9999,
                  fontSize: "0.875rem",
                }),
                option: (base, { isFocused }) => ({
                  ...base,
                  fontSize: "0.875rem",
                  backgroundColor: isFocused ? "#f0f4ff" : "white",
                }),
                input: (base) => ({
                  ...base,
                  fontSize: "0.875rem",
                  color: darkmode ? "#fff" : "#111827",
                }),
                placeholder: (base) => ({
                  ...base,
                  fontSize: "0.875rem",
                  color: "#ccd4e4",
                }),
                singleValue: (base) => ({
                  ...base,
                  fontSize: "0.875rem",
                  color: darkmode ? "#fff" : "#111827",
                }),
              }}
            />

            {errors.type && (
              <p className="text-red-500 text-sm mt-1">{errors.type}</p>
            )}
          </div>

          <div>
            <MapboxAddressInput
              label="Address"
              id="address"
              value={service?.address}
              onResult={(data) => {
                setService((prev) => ({
                  ...prev,
                  // ...data,
                  address: data?.address || "",
                  location: {
                    type: "Point",
                    coordinates: [data?.lng, data?.lat],
                  },
                }));
                // if (errors.address) {
                //   setErrors((prev) => ({ ...prev, address: "" }));
                // }
              }}
            />
            {/* {errors.address && (
              <p className="text-red-500 text-sm mt-1">{errors.address}</p>
            )} */}
          </div>

          <div className="pt-4">
            {/* <DatePicker
              label="Date"
              placeholder="Select a date"
              value={
                (service.date &&
                  // new Date(service?.date).toLocaleDateString(["en-US"], {
                  new Date(service?.date).toLocaleDateString(undefined, {
                    day: "numeric",
                    month: "numeric",
                    year: "numeric",
                  })) ||
                ""
              }
              onChange={(v) => {
                setService({
                  ...service,
                  // date: v.toLocaleDateString(["en-US"], {
                  date: v.toLocaleDateString(undefined, {
                    day: "numeric",
                    month: "numeric",
                    year: "numeric",
                  }),
                });
                if (errors.date) {
                  setErrors((prev) => ({ ...prev, date: "" }));
                }
              }}
              isPastDateDisabled
              // minDate={new Date()}
            /> */}

            <DatePicker
              label="Date"
              placeholder="Select a date"
              value={formatToMMDDYYYY(service?.date)}
              onChange={(v) => {
                setService({
                  ...service,
                  date: formatToMMDDYYYY(v),
                });
                if (errors.date) {
                  setErrors((prev) => ({ ...prev, date: "" }));
                }
              }}
              isPastDateDisabled
            />

            {errors.date && (
              <p className="text-red-500 text-sm mt-1">{errors.date}</p>
            )}
          </div>

          <div className="flex gap-4 pt-4">
            <div className="flex-1 flex flex-col">
              <label
                className="text-sm text-navy-700 dark:text-white ml-2 mb-2"
                htmlFor="start-time"
              >
                Start time
              </label>
              {/* <Select
                id="start-time"
                placeholder="Select a start time"
                value={
                  (service?.startAt &&
                    new Date(
                      "1970-01-01 " + service?.startAt
                    ).toLocaleTimeString(undefined, {
                      hour: "2-digit",
                      minute: "2-digit",
                    })) ||
                  ""
                }
                onChange={(e) => {
                  setService({ ...service, startAt: e.target.value });
                  if (errors.startAt) {
                    setErrors((prev) => ({ ...prev, startAt: "" }));
                  }
                }}
                required
              >
                <option hidden disabled value=""></option>
                

                {new Array(24).fill(0).flatMap((_, hour) => {
                  return [0, 30].map((minute) => {
                    const time = new Date();
                    time.setHours(hour);
                    time.setMinutes(minute);
                    const val = time.toLocaleTimeString(undefined, {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    });
                    return (
                      <option key={`${hour}-${minute}`} value={val}>
                        {val}
                      </option>
                    );
                  });
                })}
              </Select> */}
              <Select
                id="start-time"
                placeholder="Select a start time"
                value={service?.startAt || ""}
                onChange={(e) => {
                  setService({ ...service, startAt: e.target.value });
                  if (errors.startAt) {
                    setErrors((prev) => ({ ...prev, startAt: "" }));
                  }
                }}
                required
              >
                <option hidden disabled value=""></option>
                {new Array(24).fill(0).flatMap((_, hour) =>
                  [0, 30].map((minute) => {
                    const val = formatTo12Hour(hour, minute);
                    return (
                      <option key={`${hour}-${minute}`} value={val}>
                        {val}
                      </option>
                    );
                  })
                )}
              </Select>

              {errors.startAt && (
                <p className="text-red-500 text-sm mt-1">{errors.startAt}</p>
              )}
            </div>
            <div className="flex-1 flex flex-col">
              <label
                className="text-sm text-navy-700 dark:text-white ml-2 mb-2"
                htmlFor="end-time"
              >
                End time
              </label>
              {/* <Select
                id="end-time"
                placeholder="Select a end time"
                value={
                  (service?.endAt &&
                    new Date("1970-01-01 " + service?.endAt).toLocaleTimeString(
                      undefined,
                      { hour: "2-digit", minute: "2-digit" }
                    )) ||
                  ""
                }
                onChange={(e) => {
                  setService({ ...service, endAt: e.target.value });
                  if (errors.endAt) {
                    setErrors((prev) => ({ ...prev, endAt: "" }));
                  }
                }}
                required
              >
                <option hidden disabled value=""></option>
                
                {new Array(24).fill(0).flatMap((_, hour) => {
                  return [0, 30].map((minute) => {
                    const time = new Date();
                    time.setHours(hour);
                    time.setMinutes(minute);
                    const val = time.toLocaleTimeString(undefined, {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    });
                    return (
                      <option key={`${hour}-${minute}`} value={val}>
                        {val}
                      </option>
                    );
                  });
                })}
              </Select> */}
              <Select
                id="start-time"
                placeholder="Select a start time"
                value={service?.endAt || ""}
                onChange={(e) => {
                  setService({ ...service, endAt: e.target.value });
                  if (errors.endAt) {
                    setErrors((prev) => ({ ...prev, endAt: "" }));
                  }
                }}
                required
              >
                <option hidden disabled value=""></option>
                {new Array(24).fill(0).flatMap((_, hour) =>
                  [0, 30].map((minute) => {
                    const val = formatTo12Hour(hour, minute);
                    return (
                      <option key={`${hour}-${minute}`} value={val}>
                        {val}
                      </option>
                    );
                  })
                )}
              </Select>

              {errors.endAt && (
                <p className="text-red-500 text-sm mt-1">{errors.endAt}</p>
              )}
            </div>
          </div>

          <div className="flex flex-col pt-4">
            <label
              className="text-sm text-navy-700 dark:text-white ml-2 mb-2"
              htmlFor="repeat"
            >
              Repeat schedule
            </label>
            <Select
              id="repeat"
              placeholder="Select a repeat schedule"
              value={service?.schedule || ""}
              onChange={(e) => {
                setService({ ...service, schedule: e.target.value });
                if (errors.schedule) {
                  setErrors((prev) => ({ ...prev, schedule: "" }));
                }
              }}
              required
            >
              <option hidden disabled value=""></option>
              <option value="once">One-Time</option>
              <option value="weekly">Weekly</option>
            </Select>
            {errors.schedule && (
              <p className="text-red-500 text-sm mt-1">{errors.schedule}</p>
            )}
          </div>
        </>
      )}

      <ConfirmationDialog
        isOpen={confirmationDialog.open}
        message={confirmationDialog.message}
        onClose={() => setConfirmationDialog({ open: false, message: "" })}
        onConfirm={confirmationDialog.onConfirm}
      />
    </>
  );
};

const ServiceForm = (props) => {
  const [queryParams] = useSearchParams();
  const {
    onClose,
    service,
    setService,
    selected,
    setSelected,
    getServices,
    type,
  } = props;

  console.log("services", service);

  const [tab, setTab] = useState(queryParams.get("tab") || "details");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  //validation
  const validateForm = () => {
    const newErrors = {};

    if (!service.name?.trim()) newErrors.name = "Service name is required";
    if (!service.description?.trim())
      newErrors.description = "Description name is required";
    if (!String(service.type)?.trim())
      newErrors.type = "Service type is required";
    // if (!service.address?.trim()) newErrors.address = "Address is required";
    if (!service.date?.trim()) newErrors.date = "Date is required";
    if (!service.startAt?.trim()) newErrors.startAt = "Start time is required";
    if (!service.endAt?.trim()) newErrors.endAt = "End time is required";

    if (!service.schedule?.trim())
      newErrors.schedule = "Repeat Schedule time is required";

    if (service.startAt && service.endAt) {
      const parseTime = (timeStr) => {
        const [time, modifier] = timeStr.split(" ");
        let [hours, minutes] = time.split(":").map(Number);

        if (modifier === "PM" && hours !== 12) hours += 12;
        if (modifier === "AM" && hours === 12) hours = 0;

        return hours * 60 + minutes;
      };

      const start = parseTime(service.startAt);
      const end = parseTime(service.endAt);

      if (start >= end) {
        newErrors.startAt = "Start time must be earlier than end time";
      }

      const selectedDate = new Date(service.date);
      const now = new Date();
      const isToday =
        selectedDate.getFullYear() === now.getFullYear() &&
        selectedDate.getMonth() === now.getMonth() &&
        selectedDate.getDate() === now.getDate();

      if (isToday) {
        const currentMinutes = now.getHours() * 60 + now.getMinutes();

        // ❌ Prevent start time in the past
        if (start <= currentMinutes) {
          newErrors.startAt = "Start time must be in the future";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Safe date formatting for API (no timezone shift)
  function formatDateToYYYYMMDD(date) {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${month}/${day}/${year}`; // '2025-07-28'
  }

  const onSaveChanges = async (e, skipAssign) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (tab === "details") {
      if (service?.id) {
        try {
          setLoading(true);
          const payload = {
            ...service,
            isEdit: true,
            date: service?.date ? formatDateToYYYYMMDD(service.date) : null,
          };
          const response = await AtriumApi.put(
            `/v1/services/${service?.id}`,

            payload
          );

          if (response?.status === 200) {
            setLoading(false);
            toast.success(
              response?.data?.message || "Service updated successfully"
            );
            getServices();
            skipAssign ? onClose() : setTab("assign");
          }
        } catch (error) {
          setLoading(false);
          console.error("Error updating service:", error);
          toast.error(
            error?.response?.data?.message || "Failed to update service"
          );
        }
      } else {
        try {
          setLoading(true);
          const response = await AtriumApi.post("/v1/services", service);
          if (response) {
            setLoading(false);
            setService(response?.data?.service);
            setTab("assign");
          }
          getServices();
          skipAssign ? onClose() : setTab("assign");
          toast.success("Service created successfully.");
        } catch (error) {
          setLoading(false);
          console.log("error while creating services", error);
          toast.error(
            error?.response?.data?.message || error?.response?.data?.error
          );
        }
      }
    }
  };

  // const assignPeople = (e) => {
  //   e.preventDefault();
  //   if (tab === "assign") {
  //     AtriumApi.put(`/v1/services/${service?.id}/people`, {
  //       users: selected?.map((u) => u.id),
  //     }).then((response) => {
  //       console.log("assign people response", response);

  //       onSave(response.data.service);
  //       onClose();
  //     });
  //   }
  // };

  const assignPeople = async (e) => {
    e.preventDefault();

    if (tab === "assign") {
      try {
        setLoading(true);
        const response = await AtriumApi.put(
          `/v1/services/${service?.id}/people`,
          {
            users: selected?.map((u) => u?.id),
          }
        );
        if (response) {
          setLoading(false);
          toast.success(
            response?.data?.message || "People assigned successfully"
          );
          onClose();
          getServices();
        }
      } catch (error) {
        setLoading(false);
        console.error("Failed to assign people:", error);
        toast.error(
          error?.response?.data?.message || "Failed to assign people."
        );
      }
    }
  };

  return (
    <>
      <DrawerHeader className="px-[32px] pt-6">
        <div className="font-bold text-xl text-gray-800 dark:text-white">
          {service?.id ? "Edit service" : "Create service"}
        </div>
      </DrawerHeader>
      <DrawerBody className="flex-1 px-[32px] my-6 overflow-auto">
        <div className="select-none flex gap-1 rounded-2xl bg-gray-50 dark:bg-navy-800 p-1 mb-[8px]">
          <span
            className={`text-sm inline-block px-4 py-2 font-bold rounded-2xl ${
              tab === "details"
                ? "text-gray-800 dark:text-white"
                : "bg-none text-gray-600"
            }`}
          >
            <span
              className={`text-base text-center inline-block mr-2 content-center ${
                tab === "details"
                  ? "text-white bg-brand-700"
                  : "bg-gray-200 dark:bg-navy-700"
              } rounded-full w-6 h-6`}
            >
              1
            </span>
            Setup service
          </span>
          <span
            className={`text-sm inline-block px-4 py-2 font-bold rounded-2xl ${
              tab === "assign"
                ? "text-gray-800 dark:text-white"
                : "bg-none text-gray-600"
            }`}
          >
            <span
              className={`text-base text-center inline-block mr-2 content-center ${
                tab === "assign"
                  ? "text-white bg-brand-700"
                  : "bg-gray-200 dark:bg-navy-700"
              } rounded-full w-6 h-6`}
            >
              2
            </span>
            Assign people
          </span>
        </div>

        {tab === "details" && (
          <form className="mt-4" onSubmit={onSaveChanges} noValidate={true}>
            {errors.length ? (
              <AlertPane state="error">
                Sorry, we have trouble to save your changes
              </AlertPane>
            ) : (
              ""
            )}
            <SetupForm
              service={service}
              setService={setService}
              errors={errors}
              setErrors={setErrors}
              type={type}
            />
            <div className="flex gap-4 py-6">
              {loading ? (
                <div className="flex justify-center items-center w-full h-full">
                  <Loader />
                </div>
              ) : (
                <>
                  <DarkButton type="submit">
                    {service?.id ? "Save and assign" : "Confirm and assign"}
                  </DarkButton>

                  {service?.id ? (
                    <GrayButton onClick={() => onClose()}>Cancel</GrayButton>
                  ) : (
                    <GrayButton onClick={(e) => onSaveChanges(e, true)}>
                      Skip assigning
                    </GrayButton>
                  )}
                </>
              )}
            </div>
          </form>
        )}

        {tab === "assign" && (
          <>
            <AssignForm selected={selected} setSelected={setSelected} />
            <div className="flex gap-4 py-6">
              {loading ? (
                <div className="flex justify-center items-center w-full h-full">
                  <Loader />
                </div>
              ) : (
                <>
                  <DarkButton onClick={assignPeople}>
                    {service?.id ? "Save changes" : "Confirm and create"}
                  </DarkButton>

                  {!service?.id && (
                    <GrayButton onClick={() => setTab("details")}>
                      Back
                    </GrayButton>
                  )}
                  {service?.id && (
                    <GrayButton onClick={() => onClose()}>Cancel</GrayButton>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </DrawerBody>
    </>
  );
};

export default ServiceForm;
